import React, { useEffect, useRef, useState, useMemo } from 'react';
import './index.css';
import { LocationPoint } from '../../../../../../redux/cartSlice';
import { useGoogleMaps } from '../../../../../../hooks/useGoogleMaps';
import { Spinner } from 'react-bootstrap';

interface RouteMapProps {
  points: LocationPoint[];
  accentColor: string;
  label: string;
  distance: number;
  subtitle?: string;
  height?: number;
  sessionEndPoint?: LocationPoint | null;
}

const toGoogleLatLng = (point: LocationPoint) => ({
  lat: point.lat,
  lng: point.lng,
});

interface InfoWindowData {
  position: google.maps.LatLng;
  title: string;
  coordinates: string;
  type: 'start' | 'stop' | 'end';
  stopNumber?: number;
}

const hasValidCoordinates = (point: LocationPoint): boolean => {
  return point.lat !== 0 || point.lng !== 0;
};

const arePointsEqual = (point1: LocationPoint, point2: LocationPoint): boolean => {
  return point1.lat === point2.lat && point1.lng === point2.lng;
};

const RouteMap: React.FC<RouteMapProps> = ({
  points,
  accentColor,
  label,
  distance,
  subtitle,
  height = 500,
  sessionEndPoint,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [polyline, setPolyline] = useState<google.maps.Polyline | null>(null);
  const [markers, setMarkers] = useState<google.maps.Marker[]>([]);
  const [infoWindow, setInfoWindow] = useState<google.maps.InfoWindow | null>(null);
  const [hasInitialized, setHasInitialized] = useState(false);

  const { isLoaded, loadError } = useGoogleMaps();

  const validPoints = useMemo(() => points.filter(hasValidCoordinates), [points]);

  const hasValidEndPoint = useMemo(() => {
    return sessionEndPoint && hasValidCoordinates(sessionEndPoint);
  }, [sessionEndPoint]);

  const displayPoints = useMemo(() => {
    if (validPoints.length === 0) return [];
    if (hasValidEndPoint) {
      const endPointExists = validPoints.some(p => arePointsEqual(p, sessionEndPoint!));
      if (!endPointExists) {
        return [...validPoints, sessionEndPoint!];
      }
    }
    return validPoints;
  }, [validPoints, sessionEndPoint, hasValidEndPoint]);

  const hasStartPoint = displayPoints.length >= 1;

  // Initialize map
  useEffect(() => {
    if (!isLoaded || !mapRef.current || map) return;

    const defaultCenter = hasStartPoint
      ? toGoogleLatLng(displayPoints[0])
      : { lat: 40.7128, lng: -74.006 };

    const newMap = new google.maps.Map(mapRef.current, {
      center: defaultCenter,
      zoom: 12,
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      zoomControl: true,
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }],
        },
      ],
    });
    setMap(newMap);

    const infoWin = new google.maps.InfoWindow({
      maxWidth: 250,
      pixelOffset: new google.maps.Size(0, -10),
    });
    setInfoWindow(infoWin);

    setHasInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, mapRef.current]);

  // Create info window content
  const createInfoWindowContent = (data: InfoWindowData): string => {
    const typeColors = {
      start: '#22c55e',
      stop: accentColor,
      end: '#ef4444',
    };
    const typeIcons = { start: '🚩', stop: '📍', end: '🏁' };
    const typeLabels = {
      start: 'START POINT',
      stop: data.stopNumber !== undefined ? `STOP ${data.stopNumber}` : 'WAYPOINT',
      end: 'END POINT',
    };

    return `
      <div class="delivery-route-map__info-window">
        <div class="delivery-route-map__info-header" style="border-bottom-color: ${typeColors[data.type]}">
          <span class="delivery-route-map__info-icon">${typeIcons[data.type]}</span>
          <span class="delivery-route-map__info-title">${data.title}</span>
        </div>
        <div class="delivery-route-map__info-content">
          <div class="delivery-route-map__info-type" style="color: ${typeColors[data.type]}">
            ${typeLabels[data.type]}
          </div>
          <div class="delivery-route-map__info-coordinates">
            <div class="delivery-route-map__info-coord-row">
              <span class="delivery-route-map__info-coord-label">Latitude:</span>
              <span class="delivery-route-map__info-coord-value">${data.coordinates.split(', ')[0]}</span>
            </div>
            <div class="delivery-route-map__info-coord-row">
              <span class="delivery-route-map__info-coord-label">Longitude:</span>
              <span class="delivery-route-map__info-coord-value">${data.coordinates.split(', ')[1]}</span>
            </div>
          </div>
          ${data.type === 'stop' && data.stopNumber !== undefined ? `
            <div class="delivery-route-map__info-stop-number">
              Stop #${data.stopNumber}
            </div>
          ` : ''}
        </div>
      </div>
    `;
  };

  // Clear existing markers
  const clearMarkers = () => {
    markers.forEach(marker => marker.setMap(null));
    setMarkers([]);
  };

  // Draw polyline through all GPS points
  const drawPolyline = (map: google.maps.Map, points: LocationPoint[]) => {
    if (polyline) {
      polyline.setMap(null);
    }

    const newPolyline = new google.maps.Polyline({
      path: points.map(toGoogleLatLng),
      geodesic: true,
      strokeColor: accentColor,
      strokeOpacity: 0.85,
      strokeWeight: 5,
      map: map,
    });

    setPolyline(newPolyline);
  };

  // Add ALL point markers: S for start, numbered stops in between, E for end
  const addMarkers = (map: google.maps.Map, points: LocationPoint[]) => {
    clearMarkers();
    const newMarkers: google.maps.Marker[] = [];

    points.forEach((point, index) => {
      const position = toGoogleLatLng(point);
      let markerConfig: google.maps.MarkerOptions;
      let markerType: 'start' | 'stop' | 'end';
      let stopNumber: number | undefined;

      if (index === 0) {
        // Start marker - green S
        markerType = 'start';
        markerConfig = {
          position,
          map,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 12,
            fillColor: '#22c55e',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 3,
          },
          label: {
            text: 'S',
            color: '#ffffff',
            fontSize: '12px',
            fontWeight: 'bold',
          },
          title: 'Start Point',
          animation: google.maps.Animation.DROP,
          zIndex: 1000, // always on top
        };
      } else if (hasValidEndPoint && arePointsEqual(point, sessionEndPoint!)) {
        // End marker - red E
        markerType = 'end';
        markerConfig = {
          position,
          map,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 12,
            fillColor: '#ef4444',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 3,
          },
          label: {
            text: 'E',
            color: '#ffffff',
            fontSize: '12px',
            fontWeight: 'bold',
          },
          title: 'End Point',
          animation: google.maps.Animation.DROP,
          zIndex: 999, // just below start
        };
      } else {
        // Intermediate GPS point - accent color, numbered
        markerType = 'stop';
        stopNumber = index;
        markerConfig = {
          position,
          map,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: accentColor,
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2.5,
          },
          label: {
            text: index.toString(),
            color: '#ffffff',
            fontSize: '11px',
            fontWeight: 'bold',
          },
          title: `Stop ${index}`,
          animation: google.maps.Animation.DROP,
          zIndex: index,
        };
      }

      const marker = new google.maps.Marker(markerConfig);

      marker.addListener('mouseover', () => {
        if (infoWindow) {
          infoWindow.setContent(
            createInfoWindowContent({
              position: new google.maps.LatLng(position.lat, position.lng),
              title: marker.getTitle() || 'Point',
              coordinates: `${point.lat}° N, ${point.lng}° E`,
              type: markerType,
              stopNumber,
            })
          );
          infoWindow.open(map, marker);
        }
      });

      marker.addListener('mouseout', () => infoWindow?.close());
      newMarkers.push(marker);
    });

    setMarkers(newMarkers);
  };

  // Display route: polyline + all markers
  const displayRoute = () => {
    if (!map || displayPoints.length === 0) return;

    // Draw polyline through ALL GPS points (no waypoint limit)
    drawPolyline(map, displayPoints);

    // Add ALL markers with numbers
    addMarkers(map, displayPoints);

    // Fit bounds to show all points
    const bounds = new google.maps.LatLngBounds();
    displayPoints.forEach(point => bounds.extend(toGoogleLatLng(point)));
    map.fitBounds(bounds);

    if (displayPoints.length === 1) {
      map.setZoom(14);
    }
  };

  // Update polyline color when accentColor changes
  useEffect(() => {
    if (polyline) {
      polyline.setOptions({ strokeColor: accentColor });
    }
  }, [accentColor, polyline]);

  // Re-render route when points or map changes
  useEffect(() => {
    if (map && displayPoints.length > 0 && hasInitialized) {
      displayRoute();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, displayPoints, hasInitialized]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearMarkers();
      if (infoWindow) infoWindow.close();
      if (polyline) polyline.setMap(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!displayPoints || displayPoints.length === 0) {
    return (
      <div className="delivery-route-map delivery-route-map--empty" style={{ height: `${height}px` }}>
        <span className="delivery-route-map__empty-text">No route data available</span>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="delivery-route-map delivery-route-map--error" style={{ height: `${height}px` }}>
        <span className="delivery-route-map__error-text">Error loading Google Maps</span>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="delivery-route-map delivery-route-map--loading" style={{ height: `${height}px` }}>
        <Spinner animation="border" variant="primary" />
        <span className="delivery-route-map__loading-text">Loading map...</span>
      </div>
    );
  }

  return (
    <div className="delivery-route-map">
      {/* Header */}
      <div
        className="delivery-route-map__header"
        style={{
          borderBottomColor: `${accentColor}20`,
          backgroundColor: `${accentColor}08`,
        }}
      >
        <div className="delivery-route-map__header-left">
          <div className="delivery-route-map__status-dot" style={{ backgroundColor: accentColor }} />
          <div className="delivery-route-map__title-wrapper">
            <div className="delivery-route-map__label" style={{ color: accentColor }}>
              {label}
            </div>
            {subtitle && <div className="delivery-route-map__subtitle">{subtitle}</div>}
          </div>
        </div>
        {hasValidEndPoint && distance > 0 && (
          <span className="delivery-route-map__distance-value">{distance} km</span>
        )}
      </div>

      {/* Google Maps container */}
      <div
        className="delivery-route-map__canvas"
        style={{ height: `${height - 100}px`, minHeight: `${height - 100}px` }}
      >
        <div ref={mapRef} className="delivery-route-map__google-map" />
      </div>

      {/* Coordinates footer */}
      {hasStartPoint && (
        <div className="delivery-route-map__footer">
          <div className="delivery-route-map__coordinate">
            <div className="delivery-route-map__coordinate-label">START POINT</div>
            <div className="delivery-route-map__coordinate-value">
              {displayPoints[0].lat}° N, {displayPoints[0].lng}° E
            </div>
          </div>
          {hasValidEndPoint && (
            <div className="delivery-route-map__coordinate delivery-route-map__coordinate--right">
              <div className="delivery-route-map__coordinate-label">END POINT</div>
              <div className="delivery-route-map__coordinate-value">
                {sessionEndPoint?.lat}° N, {sessionEndPoint?.lng}° E
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RouteMap;
